import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, eur, originUrl } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Citadine", "Berline", "SUV", "Break", "Utilitaire", "Sportive", "Luxe"];
const STATES = ["Roulant", "Non-roulant"];
const GEARBOX = ["Manuelle", "Automatique"];

export default function ConvoyageBooking() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    brand: "",
    model: "",
    category: "Citadine",
    state: "Roulant",
    gearbox: "Manuelle",
    pickup: "",
    dropoff: "",
    distance_km: "",
    preferred_date: "",
    notes: "",
    full_name: "",
    email: "",
    phone: "",
  });
  const [price, setPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const km = parseFloat(form.distance_km);
    if (!km || km <= 0) {
      setPrice(null);
      return;
    }
    const ctl = new AbortController();
    api
      .post("/quotes/convoyage", { distance_km: km, state: form.state }, { signal: ctl.signal })
      .then((r) => setPrice(r.data))
      .catch(() => {});
    return () => ctl.abort();
  }, [form.distance_km, form.state]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const canGoStep2 = form.brand && form.model && parseFloat(form.distance_km) > 0;
  const canPay =
    canGoStep2 && form.pickup && form.dropoff && form.preferred_date && form.full_name && form.email && form.phone;

  const submit = async () => {
    if (!canPay) {
      toast.error("Complétez tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client: {
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
        },
        convoyage: {
          brand: form.brand,
          model: form.model,
          category: form.category,
          state: form.state,
          gearbox: form.gearbox,
          pickup: { label: form.pickup },
          dropoff: { label: form.dropoff },
          distance_km: parseFloat(form.distance_km),
          preferred_date: form.preferred_date,
          notes: form.notes || null,
        },
        origin_url: originUrl(),
      };
      const { data } = await api.post("/bookings/convoyage", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      console.error(e);
      toast.error("Une erreur est survenue. Réessayez.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="convoyage-page" className="min-h-screen bg-[#050505]">
      <Header variant="convoyage" />

      <div className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.5em] gold uppercase">Service #01</div>
        <h1 className="mt-3 font-serif text-white text-4xl sm:text-5xl">Convoyage de véhicule</h1>
        <p className="mt-3 text-gray-400 max-w-2xl">
          Renseignez les détails de votre véhicule et de votre trajet. Le prix
          est calculé en direct. Un acompte de 30% sera prélevé à la réservation.
        </p>

        <div className="mt-10 grid lg:grid-cols-[1fr_360px] gap-10">
          {/* Form */}
          <div className="space-y-10">
            <Stepper step={step} />

            {step === 1 && (
              <Section title="Votre véhicule">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Marque *">
                    <input
                      data-testid="input-brand"
                      value={form.brand}
                      onChange={set("brand")}
                      placeholder="Peugeot, Audi, Tesla..."
                      className="inp"
                    />
                  </Field>
                  <Field label="Modèle *">
                    <input
                      data-testid="input-model"
                      value={form.model}
                      onChange={set("model")}
                      placeholder="308, A4, Model 3..."
                      className="inp"
                    />
                  </Field>
                  <Field label="Catégorie">
                    <select data-testid="select-category" value={form.category} onChange={set("category")} className="inp">
                      {CATEGORIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="État">
                    <select data-testid="select-state" value={form.state} onChange={set("state")} className="inp">
                      {STATES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Boîte">
                    <select data-testid="select-gearbox" value={form.gearbox} onChange={set("gearbox")} className="inp">
                      {GEARBOX.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Distance estimée (km) *">
                    <input
                      data-testid="input-distance"
                      type="number"
                      min={1}
                      value={form.distance_km}
                      onChange={set("distance_km")}
                      placeholder="ex : 250"
                      className="inp"
                    />
                  </Field>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    disabled={!canGoStep2}
                    onClick={() => setStep(2)}
                    data-testid="btn-next-step"
                    className="btn-gold"
                  >
                    Continuer
                  </button>
                </div>
              </Section>
            )}

            {step === 2 && (
              <Section title="Trajet & contact">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Adresse de départ *">
                    <input data-testid="input-pickup" value={form.pickup} onChange={set("pickup")} placeholder="Rue, ville" className="inp" />
                  </Field>
                  <Field label="Adresse d'arrivée *">
                    <input data-testid="input-dropoff" value={form.dropoff} onChange={set("dropoff")} placeholder="Rue, ville" className="inp" />
                  </Field>
                  <Field label="Date souhaitée *">
                    <input data-testid="input-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className="inp" />
                  </Field>
                  <Field label="Notes">
                    <input data-testid="input-notes" value={form.notes} onChange={set("notes")} placeholder="Instructions particulières" className="inp" />
                  </Field>
                  <Field label="Nom complet *">
                    <input data-testid="input-fullname" value={form.full_name} onChange={set("full_name")} className="inp" />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="input-email" type="email" value={form.email} onChange={set("email")} className="inp" />
                  </Field>
                  <Field label="Téléphone *">
                    <input data-testid="input-phone" value={form.phone} onChange={set("phone")} className="inp" />
                  </Field>
                </div>
                <div className="mt-6 flex justify-between">
                  <button onClick={() => setStep(1)} className="btn-outline-gold" data-testid="btn-back">
                    Retour
                  </button>
                  <button
                    disabled={!canPay || submitting}
                    onClick={submit}
                    data-testid="btn-pay"
                    className="btn-gold"
                  >
                    {submitting ? "Redirection..." : "Payer l'acompte 30%"}
                  </button>
                </div>
              </Section>
            )}
          </div>

          {/* Sticky summary */}
          <PriceSummary price={price} title="Devis instantané" mode="deposit" />
        </div>
      </div>

      <Footer />
      <style>{`.inp{ width:100%; background:#0a0a0a; border:1px solid rgba(255,255,255,.1); color:#fff; padding:12px 14px; border-radius:2px; font-family:inherit; transition:border-color .2s;}
      .inp:focus{outline:none; border-color:var(--gold);}`}</style>
    </div>
  );
}

export function Stepper({ step, labels = ["Véhicule", "Trajet & Paiement"] }) {
  return (
    <div className="flex items-center gap-4 text-sm">
      {labels.map((l, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={l} className="flex items-center gap-3">
            <div
              className={`w-8 h-8 flex items-center justify-center border ${
                done || active ? "border-[color:var(--gold)] text-black gold-bg" : "border-white/20 text-gray-400"
              }`}
              style={done || active ? {} : { background: "transparent" }}
            >
              {idx}
            </div>
            <span className={active ? "text-white" : "text-gray-500"}>{l}</span>
            {i < labels.length - 1 && <div className="w-8 h-px bg-white/10" />}
          </div>
        );
      })}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

export function Section({ title, children }) {
  return (
    <section className="bg-[#0d0d0d] border border-white/10 p-8">
      <div className="text-xs tracking-widest gold uppercase mb-6">{title}</div>
      {children}
    </section>
  );
}

export function PriceSummary({ price, title = "Devis instantané", mode = "deposit" }) {
  const total = price?.total ?? 0;
  const deposit = price?.deposit_amount ?? 0;
  return (
    <aside data-testid="price-summary" className="lg:sticky lg:top-28 h-fit">
      <div className="bg-gradient-to-br from-[#131313] to-[#0a0a0a] border border-[color:var(--gold)]/30 p-8">
        <div className="text-xs tracking-[0.4em] gold uppercase">{title}</div>
        <div className="mt-4 font-serif text-5xl text-white">
          {price ? eur(total) : "—"}
        </div>
        <div className="text-xs text-gray-500 mt-1">Total TTC de la prestation</div>

        {price?.surcharges?.length > 0 && (
          <ul className="mt-5 space-y-2 text-sm">
            {price.surcharges.map((s, i) => (
              <li key={i} className="flex justify-between text-gray-300">
                <span>{s.label}</span>
                <span className="gold">+ {eur(s.amount)}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 border-t border-white/5 pt-6">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">
            {price?.payment_mode === "full" ? "Payable à la réservation" : "Acompte à la réservation"}
          </div>
          <div className="font-serif text-3xl text-white mt-1" data-testid="deposit-amount">
            {price ? eur(deposit) : "—"}
          </div>
          {price && price.payment_mode !== "full" && (
            <div className="text-xs text-gray-500 mt-1">
              Solde restant :{" "}
              <span className="text-gray-300">{eur(total - deposit)}</span>
            </div>
          )}
        </div>

        <div className="mt-5 text-xs text-gray-500 leading-relaxed">
          Paiement sécurisé Stripe. Votre réservation reste en attente de
          validation manuelle avant d'être définitivement confirmée. Les péages,
          parkings et prestations supplémentaires éventuels seront ajoutés au solde.
        </div>
      </div>
    </aside>
  );
}
